import type { ReactNode } from "react";
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import StickyActionBar from "@/components/mobile/StickyActionBar";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";

interface ResponseFooterProps {
  lineCount: number;
  reviewed: boolean;
  onReviewedChange: (reviewed: boolean) => void;
  primaryLabel: string;
  canSubmit: boolean;
  onSubmit: () => void;
  isMobile: boolean;
  readOnly?: boolean;
  submitting?: boolean;
  secondaryActions?: ReactNode;
}

export const HELPER_COPY = "Rates are pre-filled at the ask rate; the bid amount is your rate × quantity. An RFQ is accepted or declined as one unit. All bids exclusive of GST; you are notified only if allotted.";

const reviewCopy = (lineCount: number): string =>
  lineCount === 1
    ? "I have reviewed all 1 line, its description and attachments, and confirm these rates."
    : `I have reviewed all ${lineCount} lines, their descriptions and attachments, and confirm these rates.`;

export default function ResponseFooter({ lineCount, reviewed, onReviewedChange, primaryLabel, canSubmit, onSubmit, isMobile, readOnly = false, submitting = false, secondaryActions }: ResponseFooterProps) {
  const review = (
    <Box>
      <Typography fontSize="12px" sx={{ color: V2_COLORS.textSecondary }}>
        {HELPER_COPY}
      </Typography>
      <FormControlLabel
        sx={{ mt: 1, alignItems: "flex-start", ".MuiFormControlLabel-label": { fontSize: "13px", pt: "9px" } }}
        control={<Checkbox checked={reviewed} disabled={readOnly} onChange={(event) => onReviewedChange(event.target.checked)} />}
        label={reviewCopy(lineCount)}
      />
    </Box>
  );

  const primary = (
    <Button variant="contained" disabled={!canSubmit || readOnly || submitting} onClick={onSubmit} fullWidth={isMobile} sx={{ textTransform: "none", fontWeight: 600, height: 40, px: 3 }}>
      {primaryLabel}
    </Button>
  );

  if (isMobile) {
    return (
      <>
        <Box data-testid="response-review" sx={{ px: 2, pb: 2 }}>
          {review}
          {secondaryActions && <Box sx={{ mt: 2 }}>{secondaryActions}</Box>}
        </Box>
        <StickyActionBar>{primary}</StickyActionBar>
      </>
    );
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={3}>
      <Box data-testid="response-review" sx={{ maxWidth: 560 }}>
        {review}
      </Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
        {secondaryActions}
        {primary}
      </Stack>
    </Stack>
  );
}
